import React, { useState, ChangeEvent } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import uploadIcon from "@/assets/icons/upload.svg";
import toastService from "@/services/toastService";

interface ProjectImage {
  id: string;
  file: File;
  preview: string;
}

interface FormData {
  projectName: string;
  jobType: string;
  completedYear: number;
  projectAddress: string;
  city: string;
  latitude: number;
  longitude: number;
  builderName: string;
  builderPhone: string;
  builderEmail: string;
  builderWebsite: string;
}

export default function AddNewProject() {
  const navigate = useNavigate();
  // Form state
  const [formData, setFormData] = useState<FormData>({
    projectName: "",
    jobType: "",
    completedYear: 0,
    projectAddress: "",
    city: "",
    latitude: 0,
    longitude: 0,
    builderName: "",
    builderPhone: "",
    builderEmail: "",
    builderWebsite: "",
  });

  // Project images state
  const [projectImages, setProjectImages] = useState<ProjectImage[]>([]);

  // Check if all required fields are filled
  const isFormValid =
    formData.projectName.trim() &&
    formData.jobType.trim() &&
    formData.completedYear &&
    formData.projectAddress.trim() &&
    formData.city.trim() &&
    formData.latitude &&
    formData.longitude &&
    formData.builderName.trim() &&
    formData.builderPhone.trim() &&
    formData.builderEmail.trim() &&
    formData.builderWebsite.trim();

  // Handle input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle image uploads
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages: ProjectImage[] = [];

      Array.from(e.target.files).forEach((file) => {
        // Create a unique ID for each image
        const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Create URL for preview
        const previewUrl = URL.createObjectURL(file);

        newImages.push({
          id: imageId,
          file: file,
          preview: previewUrl,
        });
      });

      setProjectImages((prev) => [...prev, ...newImages]);
    }
  };

  // Remove an image
  const removeImage = (id: string) => {
    setProjectImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      return filtered;
    });
  };

  // Handle form submission
  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log("New Project Form submitted:", formData);
    console.log("New Project Images:", projectImages);
    toastService.success("Details saved successfully");
  };

  const labelStyle = "block text-sm font-medium text-[#616161]";
  const inputStyle =
    "w-full rounded-lg border border-[#e0e0e0] px-3 py-2 placeholder-[#828282] focus:outline-none placeholder:text-sm";

  return (
    <div className="w-full mx-auto p-7 bg-white">
      <div className="w-full">
        {/* Project Information */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 14xl:grid-cols-4 gap-x-5 gap-y-6 mb-10">
          <div className="space-y-2">
            <label htmlFor="projectName" className={labelStyle}>
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              id="projectName"
              name="projectName"
              className={inputStyle}
              placeholder="Enter Project Name"
              value={formData.projectName}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="jobType" className={labelStyle}>
              Type of Job <span className="text-red-500">*</span>
            </label>
            <input
              id="jobType"
              name="jobType"
              className={inputStyle}
              placeholder="Enter Project Type"
              value={formData.jobType}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="completedYear" className={labelStyle}>
              Completed Year <span className="text-red-500">*</span>
            </label>
            <input
              id="completedYear"
              name="completedYear"
              type="number"
              className={inputStyle}
              placeholder="Project Completed Year"
              value={formData.completedYear}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="projectAddress" className={labelStyle}>
              Project Address <span className="text-red-500">*</span>
            </label>
            <input
              id="projectAddress"
              name="projectAddress"
              className={inputStyle}
              placeholder="Enter Project Address"
              value={formData.projectAddress}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="city" className={labelStyle}>
              City <span className="text-red-500">*</span>
            </label>
            <input
              id="city"
              name="city"
              className={inputStyle}
              placeholder="Enter City"
              value={formData.city}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="latitude" className={labelStyle}>
              Latitude <span className="text-red-500">*</span>
            </label>
            <input
              id="latitude"
              name="latitude"
              type="number"
              className={inputStyle}
              placeholder="Enter Latitude"
              value={formData.latitude}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="longitude" className={labelStyle}>
              Longitude <span className="text-red-500">*</span>
            </label>
            <input
              id="longitude"
              name="longitude"
              type="number"
              className={inputStyle}
              placeholder="Enter Longitude"
              value={formData.longitude}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Builder Information */}
        <div className="mb-10">
          <h2 className="text-xl font-medium mb-7">Builder Information</h2>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 14xl:grid-cols-4 gap-x-5 gap-y-6">
            <div className="space-y-2">
              <label htmlFor="builderName" className={labelStyle}>
                Builder Name <span className="text-red-500">*</span>
              </label>
              <input
                id="builderName"
                name="builderName"
                className={inputStyle}
                placeholder="Enter Builder Name"
                value={formData.builderName}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="builderPhone" className={labelStyle}>
                Builder Phone <span className="text-red-500">*</span>
              </label>
              <input
                id="builderPhone"
                name="builderPhone"
                className={inputStyle}
                placeholder="Enter Builder Phone"
                value={formData.builderPhone}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="builderEmail" className={labelStyle}>
                Builder Email <span className="text-red-500">*</span>
              </label>
              <input
                id="builderEmail"
                name="builderEmail"
                className={inputStyle}
                placeholder="Enter Builder Email"
                value={formData.builderEmail}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="builderWebsite" className={labelStyle}>
                Builder Website <span className="text-red-500">*</span>
              </label>
              <input
                id="builderWebsite"
                name="builderWebsite"
                className={inputStyle}
                placeholder="Enter Builder Website"
                value={formData.builderWebsite}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Project Images */}
        <div className="mb-8">
          <h2 className="text-xl font-medium mb-4">Project Images</h2>

          <div className="flex flex-wrap gap-5">
            {/* Display uploaded images */}
            {projectImages.map((image, index) => (
              <div
                key={image.id}
                className="w-64 h-44 relative rounded-xl overflow-hidden border border-[#dadada]"
              >
                <img
                  src={image.preview}
                  alt={`image ${(index + 1).toString()}`}
                  className="w-64 h-44 object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className="absolute top-2 right-2 bg-slate-100 rounded-full p-1 shadow-md"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            ))}

            {/* Upload button */}
            <div className="border border-dashed border-[#dadada] rounded-xl flex flex-col items-center justify-center w-64 h-44 cursor-pointer">
              <label
                htmlFor="image-upload"
                className="flex items-center justify-center w-full h-full cursor-pointer gap-2"
              >
                <span className="text-base font-medium text-[#363636]">
                  Upload
                </span>
                <img src={uploadIcon} alt="upload icon" />
                <input
                  id="image-upload"
                  type="file"
                  multiple
                   accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => navigate(`/profile/company-profile`)}
            className="px-11 py-3 border border-bmpGreen-500 rounded-lg text-lg font-semibold text-bmpGreen-500 bg-white hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`px-11 py-3 rounded-lg text-lg font-semibold text-white bg-bmpGreen-500 hover:bg-bmpGreen-600 ${isFormValid ? "" : "cursor-not-allowed"}`}
          >
            Save Details
          </Button>
        </div>
      </div>
    </div>
  );
}
